/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { MessagesProjectsOrganisationProject } from '../models/MessagesProjectsOrganisationProject';
import type { MessagesProjectsProject } from '../models/MessagesProjectsProject';
import type { MessagesProjectsProjectUser } from '../models/MessagesProjectsProjectUser';
import type { MessagesSubscriptionsOrganisationUser } from '../models/MessagesSubscriptionsOrganisationUser';
import type { MessagesSubscriptionsOrganisationWorkspace } from '../models/MessagesSubscriptionsOrganisationWorkspace';
import type { MessagesWorkspacesMemberRole } from '../models/MessagesWorkspacesMemberRole';

import type { CancelablePromise } from '../core/CancelablePromise';
import type { BaseHttpRequest } from '../core/BaseHttpRequest';

export class ProjectsApiService {

    constructor(public readonly httpRequest: BaseHttpRequest) {}

    /**
     *  (Authorised Roles: ProjectLeader, PlatformAdministrator, SuperUser)
     * @param projectId
     * @returns MessagesProjectsProjectUser OK
     * @throws ApiError
     */
    public getProjectsApiGetUsers(
        projectId?: number,
    ): CancelablePromise<Array<MessagesProjectsProjectUser>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/rest/projects/users',
            query: {
                'projectId': projectId,
            },
        });
    }

    /**
     *  (Authorised Roles: ProjectAdministrator, ProjectLeader, PlatformAdministrator, SuperUser)
     * @param requestBody
     * @returns MessagesProjectsProjectUser OK
     * @throws ApiError
     */
    public putProjectsApiAddUserToProject(
        requestBody?: Array<MessagesWorkspacesMemberRole>,
    ): CancelablePromise<MessagesProjectsProjectUser> {
        return this.httpRequest.request({
            method: 'PUT',
            url: '/rest/projects/users',
            body: requestBody,
            mediaType: 'application/json-patch+json',
        });
    }

    /**
     *  (Authorised Roles: ProjectAdministrator, ProjectLeader, PlatformAdministrator, SuperUser)
     * @param requestBody
     * @returns MessagesProjectsProjectUser OK
     * @throws ApiError
     */
    public deleteProjectsApiRemoveUserFromProject(
        requestBody?: Array<MessagesWorkspacesMemberRole>,
    ): CancelablePromise<MessagesProjectsProjectUser> {
        return this.httpRequest.request({
            method: 'DELETE',
            url: '/rest/projects/users',
            body: requestBody,
            mediaType: 'application/json-patch+json',
        });
    }

    /**
     *  (Authorised Roles: ProjectAdministrator, ProjectLeader, PlatformAdministrator, SuperUser)
     * @param projectId
     * @param userId
     * @param requestBody
     * @returns MessagesSubscriptionsOrganisationWorkspace OK
     * @throws ApiError
     */
    public putProjectsApiUpdateUserFromProject(
        projectId?: number,
        userId?: number,
        requestBody?: Array<MessagesWorkspacesMemberRole>,
    ): CancelablePromise<MessagesSubscriptionsOrganisationWorkspace> {
        return this.httpRequest.request({
            method: 'PUT',
            url: '/rest/projects/users/update',
            query: {
                'projectId': projectId,
                'userId': userId,
            },
            body: requestBody,
            mediaType: 'application/json-patch+json',
        });
    }

    /**
     * Lists all projects in an organisation. (Authorised Roles: ProjectAdministrator, ProjectLeader, PlatformAdministrator, SuperUser)
     * @param orgId The ID of the organisation.
     * @returns MessagesProjectsOrganisationProject OK
     * @throws ApiError
     */
    public getProjectsApiListOrganisationProjects(
        orgId?: number,
    ): CancelablePromise<Array<MessagesProjectsOrganisationProject>> {
        return this.httpRequest.request({
            method: 'GET',
            url: '/rest/projects/organisation/projects',
            query: {
                'orgId': orgId,
            },
        });
    }

    /**
     * Adds a new project to an organisation. (Authorised Roles: ProjectLeader, PlatformAdministrator, SuperUser)
     * @param requestBody The project to add.
     * @returns MessagesProjectsProject OK
     * @throws ApiError
     */
    public putProjectsApiAddProject(
        requestBody?: MessagesProjectsProject,
    ): CancelablePromise<Array<MessagesProjectsProject>> {
        return this.httpRequest.request({
            method: 'PUT',
            url: '/rest/projects/organisation/projects',
            body: requestBody,
            mediaType: 'application/json-patch+json',
        });
    }

    /**
     * Updates an existing project within an organisation. (Authorised Roles: ProjectLeader, PlatformAdministrator, SuperUser)
     * @param requestBody The project with updated information.
     * @returns MessagesProjectsProject OK
     * @throws ApiError
     */
    public postProjectsApiUpdateProject(
        requestBody?: MessagesProjectsProject,
    ): CancelablePromise<Array<MessagesProjectsProject>> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/rest/projects/organisation/projects',
            body: requestBody,
            mediaType: 'application/json-patch+json',
        });
    }

    /**
     * Freezes a project, making it read-only. (Authorised Roles: PlatformAdministrator, SuperUser)
     * @param requestBody The project to freeze.
     * @returns MessagesProjectsProject OK
     * @throws ApiError
     */
    public postProjectsApiFreezeProject(
        requestBody?: MessagesProjectsProject,
    ): CancelablePromise<Array<MessagesProjectsProject>> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/rest/projects/organisation/projects/freeze',
            body: requestBody,
            mediaType: 'application/json-patch+json',
        });
    }

    /**
     * Unfreezes a project, making it editable again. (Authorised Roles: SuperUser)
     * @param requestBody The project to unfreeze.
     * @returns MessagesProjectsProject OK
     * @throws ApiError
     */
    public postProjectsApiUnfreezeProject(
        requestBody?: MessagesProjectsProject,
    ): CancelablePromise<Array<MessagesProjectsProject>> {
        return this.httpRequest.request({
            method: 'POST',
            url: '/rest/projects/organisation/projects/unfreeze',
            body: requestBody,
            mediaType: 'application/json-patch+json',
        });
    }

    /**
     * Adds a new administrator to a project. (Authorised Roles: ProjectLeader, PlatformAdministrator, SuperUser)
     * @param projectId The ID of the project.
     * @param adminId The ID of the user to add as an administrator.
     * @returns MessagesSubscriptionsOrganisationUser OK
     * @throws ApiError
     */
    public putProjectsApiAddProjectAdmin(
        projectId?: number,
        adminId?: number,
    ): CancelablePromise<MessagesSubscriptionsOrganisationUser> {
        return this.httpRequest.request({
            method: 'PUT',
            url: '/rest/projects/organisation/projects/admins',
            query: {
                'projectId': projectId,
                'adminId': adminId,
            },
        });
    }

    /**
     * Removes an administrator from a project. (Authorised Roles: ProjectLeader, PlatformAdministrator, SuperUser)
     * @param projectId The ID of the project.
     * @param adminId The ID of the administrator to remove.
     * @returns MessagesSubscriptionsOrganisationUser OK
     * @throws ApiError
     */
    public deleteProjectsApiRemoveProjectAdmin(
        projectId?: number,
        adminId?: number,
    ): CancelablePromise<MessagesSubscriptionsOrganisationUser> {
        return this.httpRequest.request({
            method: 'DELETE',
            url: '/rest/projects/organisation/projects/admins',
            query: {
                'projectId': projectId,
                'adminId': adminId,
            },
        });
    }

    /**
     * Adds a new leader to a project. (Authorised Roles: PlatformAdministrator, SuperUser)
     * @param projectId The ID of the project.
     * @param adminId The ID of the user to add as a leader.
     * @returns MessagesSubscriptionsOrganisationUser OK
     * @throws ApiError
     */
    public putProjectsApiAddProjectLeader(
        projectId?: number,
        adminId?: number,
    ): CancelablePromise<MessagesSubscriptionsOrganisationUser> {
        return this.httpRequest.request({
            method: 'PUT',
            url: '/rest/projects/organisation/projects/leaders',
            query: {
                'projectId': projectId,
                'adminId': adminId,
            },
        });
    }

    /**
     * Removes a leader from a project. (Authorised Roles: PlatformAdministrator, SuperUser)
     * @param projectId The ID of the project.
     * @param adminId The ID of the leader to remove.
     * @returns MessagesSubscriptionsOrganisationUser OK
     * @throws ApiError
     */
    public deleteProjectsApiRemoveProjectLeader(
        projectId?: number,
        adminId?: number,
    ): CancelablePromise<MessagesSubscriptionsOrganisationUser> {
        return this.httpRequest.request({
            method: 'DELETE',
            url: '/rest/projects/organisation/projects/leaders',
            query: {
                'projectId': projectId,
                'adminId': adminId,
            },
        });
    }

}
